import { useSelector } from "react-redux";
import { FaLocationDot, FaUser } from "react-icons/fa6";
import { MdAccessTime, MdClose, MdLink } from "react-icons/md";
import { useState, useMemo } from "react";
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import { deepOrange, deepPurple, green, pink, blue } from '@mui/material/colors';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const PersonalEvents = () => {
    const UserEvent = useSelector((state) => state?.calender?.personalEventsData) || [];
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('past'); // 'past' or 'upcoming'

    console.log("Personal Events Data:", UserEvent);

    // Filter events based on selected time range
    const filteredEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const twelveMonthsAgo = new Date(today);
        twelveMonthsAgo.setMonth(today.getMonth() - 12);
        
        const twelveMonthsLater = new Date(today);
        twelveMonthsLater.setMonth(today.getMonth() + 12);
        
        if (timeRange === 'past') {
            return UserEvent.filter(event => {
                const eventDate = new Date(event.startTime);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate >= twelveMonthsAgo && eventDate < today;
            });
        } else {
            return UserEvent.filter(event => {
                const eventDate = new Date(event.startTime);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate >= today && eventDate <= twelveMonthsLater;
            });
        }
    }, [UserEvent, timeRange]);

    // Array of colors for avatars
    const avatarColors = [deepOrange[500], deepPurple[500], green[500], pink[500], blue[500]];
    
    // Host avatar colors for default logos
    const hostAvatarColors = ['#0044C6', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4'];

    // Helper function to get initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Helper function to get host initials for default avatar
    const getHostInitials = (hostName) => {
        if (!hostName) return 'H';
        return hostName
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Helper function to format date
    const formatEventDate = (dateString) => {
        const date = new Date(dateString);
        const options = { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        };
        return date.toLocaleDateString('en-US', options).replace(/,/g, '');
    };

    // Helper function to format full date and time for drawer
    const formatFullDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true 
        });
    };

    // Helper function to format time
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
        });
    };

    // Handle event click
    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setDrawerOpen(true);
    };

    // Handle drawer close
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setTimeout(() => setSelectedEvent(null), 300);
    };

    // Handle location click - opens in Google Maps
    const handleLocationClick = (location) => {
        if (location) {
            const encodedLocation = encodeURIComponent(location);
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodedLocation}`, '_blank');
        }
    };

    // Handle meet link click
    const handleMeetLinkClick = (meetLink) => {
        if (meetLink) {
            window.open(meetLink, '_blank', 'noopener,noreferrer');
        }
    };

    // Handle time range change
    const handleTimeRangeChange = (event, newTimeRange) => {
        if (newTimeRange !== null) {
            setTimeRange(newTimeRange);
        }
    };

    // Group events by month and year (with latest events first in each month)
    const groupEventsByMonth = (events) => {
        const grouped = {};
        
        // Sort events by date (for past: latest first, for upcoming: earliest first)
        const sortedEvents = [...events].sort((a, b) => {
            if (timeRange === 'past') {
                return new Date(b.startTime) - new Date(a.startTime);
            } else {
                return new Date(a.startTime) - new Date(b.startTime);
            }
        });
        
        sortedEvents.forEach(event => {
            const date = new Date(event.startTime);
            const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            
            if (!grouped[monthYear]) {
                grouped[monthYear] = [];
            }
            grouped[monthYear].push(event);
        });
        
        return grouped;
    };

    // Sort events within a month
    const sortEventsByDate = (events) => {
        return [...events].sort((a, b) => {
            if (timeRange === 'past') {
                return new Date(b.startTime) - new Date(a.startTime);
            } else {
                return new Date(a.startTime) - new Date(b.startTime);
            }
        });
    };

    const groupedEvents = groupEventsByMonth(filteredEvents);

    // Get current date range for display
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twelveMonthsAgo = new Date(today);
    twelveMonthsAgo.setMonth(today.getMonth() - 12);
    const twelveMonthsLater = new Date(today);
    twelveMonthsLater.setMonth(today.getMonth() + 12);
    
    const dateRangeText = timeRange === 'past' 
        ? `${twelveMonthsAgo.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
        : `${today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${twelveMonthsLater.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;

    return (
        <>
            <div style={{ 
                padding: '16px',
                maxWidth: '1400px',
                margin: '0 auto',
                width: '100%'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '2px solid #0044C6',
                    paddingBottom: '10px',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <h1 style={{ 
                        fontSize: '24px', 
                        color: '#333',
                        fontWeight: '600',
                        margin: 0
                    }}>
                        Personal Events
                    </h1>
                    
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <ToggleButtonGroup
                            value={timeRange}
                            exclusive
                            onChange={handleTimeRangeChange}
                            aria-label="time range"
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    fontSize: '12px',
                                    padding: '4px 12px',
                                    fontWeight: 500
                                },
                                '& .Mui-selected': {
                                    backgroundColor: '#0044C6 !important',
                                    color: 'white !important',
                                    '&:hover': {
                                        backgroundColor: '#0033a0 !important'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="past" aria-label="last 12 months">
                                Last 12 Months
                            </ToggleButton>
                            <ToggleButton value="upcoming" aria-label="upcoming 12 months">
                                Upcoming 12 Months
                            </ToggleButton>
                        </ToggleButtonGroup>
                        
                        <Chip 
                            label={timeRange === 'past' ? `Past: ${dateRangeText}` : `Upcoming: ${dateRangeText}`}
                            sx={{ 
                                backgroundColor: '#E3F2FD',
                                color: '#0044C6',
                                fontWeight: 500,
                                fontSize: '14px'
                            }} 
                        />
                    </div>
                </div>
                
                {Object.keys(groupedEvents).length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 8,
                        px: 2,
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px'
                    }}>
                        <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                            No events found in the {timeRange === 'past' ? 'last 12 months' : 'upcoming 12 months'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#999' }}>
                            Events from {dateRangeText} will appear here
                        </Typography>
                    </Box>
                ) : (
                    Object.entries(groupedEvents).map(([monthYear, events]) => (
                        <div key={monthYear} style={{ marginBottom: '30px' }}>
                            <h2 style={{ 
                                fontSize: '20px', 
                                marginBottom: '15px',
                                color: '#0044C6',
                                backgroundColor: '#E3F2FD',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontWeight: '500',
                                borderLeft: '4px solid #0044C6',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span>{monthYear} <span style={{ color: '#666', fontSize: '14px', marginLeft: '8px' }}>({events.length})</span></span>
                                <Chip 
                                    label={timeRange === 'past' ? "Latest First" : "Earliest First"}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: '#0044C6',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '11px',
                                        height: '24px'
                                    }} 
                                />
                            </h2>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '12px',
                                width: '100%'
                            }}>
                                {sortEventsByDate(events).map(event => (
                                    <div 
                                        key={event.id} 
                                        onClick={() => handleEventClick(event)}
                                        style={{
                                            backgroundColor: 'white',
                                            border: '1px solid #eaeaea',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: 'auto',
                                            minHeight: '160px',
                                            maxHeight: '220px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 68, 198, 0.15)';
                                            e.currentTarget.style.borderColor = '#0044C6';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.03)';
                                            e.currentTarget.style.borderColor = '#eaeaea';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        {/* Check if event is today or upcoming */}
                                        {(() => {
                                            const eventDate = new Date(event.startTime);
                                            eventDate.setHours(0, 0, 0, 0);
                                            const isToday = eventDate.getTime() === today.getTime();
                                            const isPast = eventDate < today;
                                            
                                            if (timeRange === 'upcoming' && isToday) {
                                                return (
                                                    <Chip 
                                                        label="Today" 
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: '#FF9800',
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '9px',
                                                            height: '18px',
                                                            position: 'absolute',
                                                            top: '8px',
                                                            right: '8px'
                                                        }} 
                                                    />
                                                );
                                            }
                                            return null;
                                        })()}
                                        
                                        {/* Title - On Top */}
                                        <div style={{ 
                                            fontSize: '15px', 
                                            fontWeight: '600', 
                                            marginBottom: '6px',
                                            color: '#333',
                                            lineHeight: '1.3',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {event.title || 'Untitled Event'}
                                        </div>
                                        
                                        {/* Host Section */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '8px',
                                            paddingBottom: '6px',
                                            borderBottom: '1px solid #f0f0f0'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <Avatar
                                                    src={event.host?.profile_pic}
                                                    sx={{ 
                                                        width: 20, 
                                                        height: 20,
                                                        bgcolor: event.host?.profile_pic ? 'transparent' : hostAvatarColors[Math.floor(Math.random() * hostAvatarColors.length)],
                                                        fontSize: '10px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {!event.host?.profile_pic && (
                                                        event.host?.name ? 
                                                            getHostInitials(event.host.name) : 
                                                            <FaUser size={10} />
                                                    )}
                                                </Avatar>
                                                
                                                <Typography sx={{
                                                    fontSize: '11px',
                                                    color: '#0044C6',
                                                    fontWeight: 500,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '120px'
                                                }}>
                                                    {event.host?.name || event.createdBy?.split('@')[0] || 'Unknown'}
                                                </Typography>
                                            </div>
                                            
                                            <Chip
                                                label="Host"
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: '#E3F2FD',
                                                    color: '#0044C6',
                                                    fontWeight: 600,
                                                    fontSize: '9px',
                                                    height: '18px',
                                                    '& .MuiChip-label': {
                                                        px: '6px'
                                                    }
                                                }} 
                                            />
                                        </div>

                                        <div style={{ 
                                            fontSize: '11px', 
                                            fontWeight: '500', 
                                            color: new Date(event.startTime).setHours(0,0,0,0) === today.getTime() && timeRange === 'upcoming' ? '#FF9800' : '#0044C6',
                                            marginBottom: '6px',
                                            backgroundColor: new Date(event.startTime).setHours(0,0,0,0) === today.getTime() && timeRange === 'upcoming' ? '#FFF3E0' : '#E3F2FD',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            display: 'inline-block',
                                            alignSelf: 'flex-start'
                                        }}>
                                            {formatEventDate(event.startTime)}
                                        </div>
                                        
                                        <div style={{ 
                                            fontSize: '11px', 
                                            color: '#666',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            marginBottom: '6px',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{ 
                                                backgroundColor: '#f5f5f5',
                                                padding: '2px 6px',
                                                borderRadius: '10px',
                                                color: '#555',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '2px',
                                                fontSize: '10px'
                                            }}>
                                                <span style={{ fontSize: '9px' }}><MdAccessTime/></span> {formatTime(event.startTime)}
                                            </span>
                                            <span style={{ color: '#999' }}>-</span>
                                            <span style={{ 
                                                backgroundColor: '#f5f5f5',
                                                padding: '2px 6px',
                                                borderRadius: '10px',
                                                color: '#555',
                                                fontSize: '10px'
                                            }}>
                                                {formatTime(event.endTime)}
                                            </span>
                                        </div>
                                        
                                        {event.location && (
                                            <div 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLocationClick(event.location);
                                                }}
                                                style={{ 
                                                    marginBottom: '4px',
                                                    fontSize: '10px',
                                                    color: '#666',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    cursor: 'pointer'
                                                }}
                                                title="Click to open in Google Maps"
                                            >
                                                <span style={{ fontSize: '9px' }}><FaLocationDot color="#0044C6"/></span> 
                                                <span style={{ 
                                                    backgroundColor: '#f5f5f5',
                                                    padding: '2px 6px',
                                                    borderRadius: '10px',
                                                    fontSize: '10px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: '120px',
                                                    color: '#0044C6',
                                                    textDecoration: 'underline',
                                                    textDecorationColor: '#0044C6',
                                                    textUnderlineOffset: '2px'
                                                }}>
                                                    {event.location}
                                                </span>
                                            </div>
                                        )}
                                        
                                        {/* Attendees Avatar Group */}
                                        {event.attendees && event.attendees.length > 0 && (
                                            <div style={{ 
                                                marginTop: 'auto',
                                                marginBottom: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                borderTop: '1px solid #f0f0f0',
                                                paddingTop: '6px'
                                            }}>
                                                <span style={{ 
                                                    fontSize: '10px', 
                                                    color: '#666',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <span>👥</span>
                                                    <span style={{
                                                        backgroundColor: '#0044C6',
                                                        color: 'white',
                                                        borderRadius: '8px',
                                                        padding: '2px 5px',
                                                        fontSize: '9px',
                                                        fontWeight: '500'
                                                    }}>
                                                        {event.attendees.length}
                                                    </span>
                                                </span>
                                                
                                                <AvatarGroup 
                                                    max={4} 
                                                    spacing="small"
                                                    sx={{
                                                        '& .MuiAvatar-root': {
                                                            width: 24,
                                                            height: 24,
                                                            fontSize: '10px',
                                                            fontWeight: 600,
                                                            border: '2px solid white'
                                                        }
                                                    }}
                                                >
                                                    {event.attendees.map((attendee, index) => (
                                                        <Avatar
                                                            key={attendee.email || index}
                                                            alt={attendee.name || 'Attendee'}
                                                            src={attendee.profile_pic}
                                                            sx={{ 
                                                                bgcolor: attendee.profile_pic ? 'transparent' : avatarColors[index % avatarColors.length],
                                                                color: attendee.profile_pic ? 'inherit' : 'white'
                                                            }}
                                                        >
                                                            {!attendee.profile_pic && getInitials(attendee.name)}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            </div>
                                        )}
                                        
                                        {event.description && !event.attendees && (
                                            <div style={{ 
                                                marginTop: 'auto',
                                                fontSize: '10px',
                                                color: '#777',
                                                borderTop: '1px solid #f0f0f0',
                                                paddingTop: '4px',
                                                marginTop: '4px',
                                                lineHeight: '1.3',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {event.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Event Details Drawer - Keep the same as before */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerClose}
                PaperProps={{
                    sx: {
                        width: '400px',
                        maxWidth: '90vw',
                        borderTopLeftRadius: '16px',
                        borderBottomLeftRadius: '16px',
                        padding: '24px'
                    }
                }}
            >
                {selectedEvent && (
                    <Box>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mb: 3
                        }}>
                            <Typography variant="h5" sx={{ 
                                fontWeight: 600,
                                color: '#0044C6',
                                fontSize: '1.5rem'
                            }}>
                                Event Details
                            </Typography>
                            <IconButton onClick={handleDrawerClose} sx={{ 
                                backgroundColor: '#f5f5f5',
                                '&:hover': { backgroundColor: '#e0e0e0' }
                            }}>
                                <MdClose />
                            </IconButton>
                        </Box>

                        <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            mb: 2,
                            fontSize: '1.8rem',
                            color: '#333',
                            lineHeight: 1.2
                        }}>
                            {selectedEvent.title || 'Untitled Event'}
                        </Typography>

                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 3,
                            pb: 2,
                            borderBottom: '2px solid #E3F2FD'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                    src={selectedEvent.host?.profile_pic}
                                    sx={{ 
                                        width: 32, 
                                        height: 32,
                                        bgcolor: selectedEvent.host?.profile_pic ? 'transparent' : hostAvatarColors[Math.floor(Math.random() * hostAvatarColors.length)],
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {!selectedEvent.host?.profile_pic && (
                                        selectedEvent.host?.name ? 
                                            getHostInitials(selectedEvent.host.name) : 
                                            <FaUser size={16} />
                                    )}
                                </Avatar>
                                <Box>
                                    <Typography sx={{ 
                                        fontWeight: 600, 
                                        color: '#0044C6',
                                        fontSize: '1rem',
                                        lineHeight: 1.2
                                    }}>
                                        {selectedEvent.host?.name || selectedEvent.createdBy?.split('@')[0] || 'Unknown'}
                                    </Typography>
                                    {selectedEvent.createdBy && (
                                        <Typography variant="caption" sx={{ color: '#999', fontSize: '10px' }}>
                                            {selectedEvent.createdBy}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            
                            <Chip
                                label="Host"
                                sx={{ 
                                    backgroundColor: '#E3F2FD',
                                    color: '#0044C6',
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    height: '24px'
                                }} 
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                            <Chip 
                                label={selectedEvent.eventType || 'Personal Event'} 
                                sx={{ 
                                    backgroundColor: '#E3F2FD',
                                    color: '#0044C6',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    height: '24px'
                                }} 
                            />
                            {selectedEvent.meetingType && (
                                <Chip 
                                    label={selectedEvent.meetingType}
                                    sx={{ 
                                        backgroundColor: '#FF9800',
                                        color: 'white',
                                        fontWeight: 500,
                                        fontSize: '12px',
                                        height: '24px'
                                    }} 
                                />
                            )}
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" sx={{ 
                                fontWeight: 600, 
                                color: '#666',
                                mb: 1,
                                fontSize: '0.9rem'
                            }}>
                                Date & Time
                            </Typography>
                            <Box sx={{ 
                                backgroundColor: '#f8f9fa',
                                p: 1.5,
                                borderRadius: '10px'
                            }}>
                                <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>
                                    {formatFullDateTime(selectedEvent.startTime)} - {formatTime(selectedEvent.endTime)}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Duration: {selectedEvent.duration} minutes
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.25 }}>
                                    Timezone: {selectedEvent.timeZone || 'Asia/Kolkata'}
                                </Typography>
                            </Box>
                        </Box>

                        {selectedEvent.location && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 1,
                                    fontSize: '0.9rem'
                                }}>
                                    Location
                                </Typography>
                                <Box 
                                    onClick={() => handleLocationClick(selectedEvent.location)}
                                    sx={{ 
                                        backgroundColor: '#f8f9fa',
                                        p: 1.5,
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            borderColor: '#0044C6'
                                        }
                                    }}
                                    title="Click to open in Google Maps"
                                >
                                    <FaLocationDot color="#0044C6" size={14} />
                                    <Typography sx={{ 
                                        color: '#0044C6',
                                        textDecoration: 'underline',
                                        textDecorationColor: '#0044C6',
                                        textUnderlineOffset: '2px',
                                        fontSize: '0.9rem'
                                    }}>
                                        {selectedEvent.location}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {selectedEvent.meetLink && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 1,
                                    fontSize: '0.9rem'
                                }}>
                                    Meeting Link
                                </Typography>
                                <Box 
                                    onClick={() => handleMeetLinkClick(selectedEvent.meetLink)}
                                    sx={{ 
                                        backgroundColor: '#f8f9fa',
                                        p: 1.5,
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: '#e3f2fd',
                                            borderColor: '#0044C6'
                                        }
                                    }}
                                    title="Click to open meeting link"
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <MdLink color="#0044C6" size={14} />
                                        <Typography sx={{ 
                                            color: '#0044C6',
                                            fontWeight: 500,
                                            fontSize: '0.85rem'
                                        }}>
                                            Join Meeting
                                        </Typography>
                                    </Box>
                                    <Typography 
                                        sx={{ 
                                            color: '#0044C6',
                                            textDecoration: 'underline',
                                            textDecorationColor: '#0044C6',
                                            textUnderlineOffset: '2px',
                                            wordBreak: 'break-all',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {selectedEvent.meetLink}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {selectedEvent.description && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 1,
                                    fontSize: '0.9rem'
                                }}>
                                    Description
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: '#f8f9fa',
                                    p: 1.5,
                                    borderRadius: '10px'
                                }}>
                                    <Typography sx={{ fontSize: '0.9rem' }}>{selectedEvent.description}</Typography>
                                </Box>
                            </Box>
                        )}

                        {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="subtitle2" sx={{ 
                                    fontWeight: 600, 
                                    color: '#666',
                                    mb: 1,
                                    fontSize: '0.9rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <span>Attendees</span>
                                    <Chip 
                                        label={selectedEvent.attendees.length} 
                                        size="small"
                                        sx={{ 
                                            backgroundColor: '#0044C6',
                                            color: 'white',
                                            fontWeight: 500,
                                            fontSize: '11px',
                                            height: '20px'
                                        }} 
                                    />
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: '#f8f9fa',
                                    p: 1.5,
                                    borderRadius: '10px'
                                }}>
                                    {selectedEvent.attendees.map((attendee, index) => (
                                        <Box 
                                            key={attendee.email || index}
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1.5,
                                                mb: index < selectedEvent.attendees.length - 1 ? 1.5 : 0,
                                                pb: index < selectedEvent.attendees.length - 1 ? 1.5 : 0,
                                                borderBottom: index < selectedEvent.attendees.length - 1 ? '1px solid #e0e0e0' : 'none'
                                            }}
                                        >
                                            <Avatar
                                                src={attendee.profile_pic}
                                                sx={{ 
                                                    width: 32, 
                                                    height: 32,
                                                    bgcolor: attendee.profile_pic ? 'transparent' : avatarColors[index % avatarColors.length]
                                                }}
                                            >
                                                {!attendee.profile_pic && getInitials(attendee.name)}
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                    {attendee.name || 'Unknown'}
                                                </Typography>
                                                {attendee.email && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        {attendee.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ mt: 3, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Created by: {selectedEvent.createdBy || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.25 }}>
                                Created at: {new Date(selectedEvent.createdAt).toLocaleString()}
                            </Typography>
                            {selectedEvent.updatedBy && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.25 }}>
                                    Updated by: {selectedEvent.updatedBy}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Drawer>
        </>
    );
};

export default PersonalEvents;